import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useMediaLibraryCreateMutation, useMediaLibraryDeleteMutation } from 'hooks/mutations/publishing'
import { mediaLibrariesOverviewRoute } from 'pages/NWPlatform/MediaLibraries'
import { mediaLibraryRoute } from 'pages/NWPlatform/MediaLibraries/MediaLibrary'
import { mediaLibraryQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mediaLibraryId } = mediaLibraryRoute.useParams()
  const navigate = useNavigate({ from: mediaLibraryRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const mediaLibraryQuery = useQuery(mediaLibraryQueryKeys.detail({ libraryId: mediaLibraryId }))
  const { data: mediaLibrary } = mediaLibraryQuery

  const { mutate: createMediaLibrary, ...mediaLibraryCreateMutation } = useMediaLibraryCreateMutation()
  const { mutate: deleteMediaLibrary } = useMediaLibraryDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!mediaLibrary) return

    setAnchorEl(undefined)

    deleteMediaLibrary(
      { libraryId: mediaLibrary.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:MediaLibrary.nameDelete', { name: mediaLibrary.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: mediaLibrariesOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteMediaLibrary, enqueueSnackbar, mediaLibrary, navigate, t])

  const handleDuplicateMediaLibrary = useCallback(() => {
    if (!mediaLibrary) return

    setAnchorEl(undefined)

    createMediaLibrary(
      {
        mediaPlatformInsert: {
          ...mediaLibrary,
          defaultWorkspaceId: mediaLibrary.defaultWorkspaceId || '',
          name: `${mediaLibrary.name} (${t('duplicate')})`,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [mediaLibrary, createMediaLibrary, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={mediaLibrary?.name}
        entity={t('entity:publishing.mediaLibrary', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateMediaLibrary}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {mediaLibraryCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={mediaLibraryId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:MediaLibrary.deleteMediaLibrary', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
